import React, {useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Button} from 'antd';
import {LineChartOutlined} from '@ant-design/icons';
import FundamentalAnalysisChart, {FundamentalAnalysisData} from '../chat/FundamentalAnalysisChart';
import TechnicalIndicatorChart, {TechnicalIndicatorChartData} from '../chat/TechnicalIndicatorChart';

// 模块级别的图表模式存储，避免组件重渲染时状态丢失
const globalChartModes = new Map<string, 'json' | 'chart'>();

// 表格级别的图表模式存储（用于技术指标表格）
const globalTableChartModes = new Map<string, boolean>();

// 简单的字符串 hash 函数
const simpleHash = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
};

// JSON格式化函数（移到组件外部）
const formatJson = (content: string): string => {
    try {
        const parsed = JSON.parse(content);
        return JSON.stringify(parsed, null, 2);
    } catch {
        return content;
    }
};

// JSON语法高亮函数（移到组件外部）
const highlightJson = (json: string): string => {
    return json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"([^"]+)":/g, '<span style="color: #9cdcfe;">"$1":</span>')
        .replace(/: "([^"]*)"/g, ': <span style="color: #ce9178;">"$1"</span>')
        .replace(/: (\d+\.?\d*)/g, ': <span style="color: #b5cea8;">$1</span>')
        .replace(/: (true|false)/g, ': <span style="color: #569cd6;">$1</span>')
        .replace(/: (null)/g, ': <span style="color: #569cd6;">$1</span>');
};

// 检测内容是否为JSON（移到组件外部）
const isJsonContent = (content: string): boolean => {
    const trimmed = content.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) return true;
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) return true;
    return false;
};

// 检测 JSON 是否为 FundamentalAnalysis 类型（移到组件外部）
const isFundamentalAnalysis = (content: string): { isFA: boolean; data?: FundamentalAnalysisData } => {
    try {
        const parsed = JSON.parse(content);
        if (parsed && parsed.type === 'FundamentalAnalysis' && parsed.instId && parsed.timeframe) {
            return {isFA: true, data: parsed as FundamentalAnalysisData};
        }
    } catch {
        // 解析失败，不是有效 JSON
    }
    return {isFA: false};
};

/**
 * 创建技术指标表格检测函数（工厂函数，用于捕获 segmentTitle 和 extractedTitles）
 */
const createTechnicalIndicatorTableDetector = (
    segmentTitle?: string,
    extractedTitles?: Array<{ instId: string; timeframe: string; title: string }>
) => {
    return function isTechnicalIndicatorTable(tableText: string): {
        isTI: boolean;
        data?: TechnicalIndicatorChartData
    } {
        try {
            // console.log('[技术指标检测] 输入表格文本:', tableText.substring(0, 300));
            // console.log('[技术指标检测] segmentTitle:', segmentTitle);
            // console.log('[技术指标检测] extractedTitles:', extractedTitles);

            let instId = '';
            let timeframe = '';
            let hasIndicatorRow = false;
            let hasOHLCRow = false;

            // 尝试从原始文本中匹配标题（支持 # 或 ### 开头）
            const titleMatch = tableText.match(/#+\s*([A-Z]{2,}-[A-Z]{2,}-[A-Z]+)-(\d+[a-zA-Z]+)\s*技术指标表格/i);
            if (titleMatch) {
                instId = titleMatch[1];
                timeframe = titleMatch[2];
                // console.log('[技术指标检测] 从标题匹配到:', { instId, timeframe });
            }

            // 检测是否包含技术指标行（EMA, RSI, BOLL 等）
            const indicatorPatterns = [
                /EMA\s*\(\d+\)/i,
                /SMA\s*\(\d+\)/i,
                /RSI\s*\(\d+\)/i,
                /BOLL\s*\(\d+\)/i,
                /MACD\s*\(/i,
                /KDJ\s*\(\d+\)/i,
                /CCI\s*\(\d+\)/i,
                /ADX\s*\(\d+\)/i
            ];
            hasIndicatorRow = indicatorPatterns.some(pattern => pattern.test(tableText));

            // 检测是否包含 OHLC 行
            hasOHLCRow = /open|high|low|close/i.test(tableText);

            // 如果是 JSON 格式，尝试从 rows 中提取信息
            try {
                const parsed = JSON.parse(tableText);
                if (parsed.headers && Array.isArray(parsed.headers)) {
                    // 检查表头是否包含时间周期（如 "4H", "1d" 等）
                    const timeframeHeader = parsed.headers.find((h: string) => /^\d+[a-zA-Z]+$/i.test(h.trim()));
                    if (timeframeHeader && !timeframe) {
                        timeframe = timeframeHeader.trim().toLowerCase();
                        // console.log('[技术指标检测] 从表头匹配到 timeframe:', timeframe);
                    }

                    // 检查第一个表头是否是合约 ID 格式（如 ETH-USDT-SWAP）
                    if (!instId && parsed.headers.length > 0) {
                        const firstHeader = parsed.headers[0];
                        const headerInstIdMatch = firstHeader.match(/([A-Z]{2,}-[A-Z]{2,}-[A-Z]+)/i);
                        if (headerInstIdMatch) {
                            instId = headerInstIdMatch[1];
                            // console.log('[技术指标检测] 从表头匹配到 instId:', instId);
                        }
                    }
                }

                // 检查 rows 中是否包含技术指标
                if (parsed.rows && Array.isArray(parsed.rows)) {
                    for (const row of parsed.rows) {
                        if (Array.isArray(row) && row.length > 0) {
                            const firstCell = row[0].toLowerCase();
                            // 检查是否是技术指标行
                            for (const pattern of indicatorPatterns) {
                                if (pattern.test(firstCell)) {
                                    hasIndicatorRow = true;
                                    // console.log('[技术指标检测] 在 rows 中找到指标:', firstCell);
                                    break;
                                }
                            }
                            // 检查是否是 OHLC 行
                            if (['open', 'high', 'low', 'close', 'volume'].some(k => firstCell.includes(k))) {
                                hasOHLCRow = true;
                            }
                            // 尝试从行中提取合约 ID（如 ETH-USDT-SWAP）
                            if (!instId) {
                                const instIdMatch = row[0].match(/([A-Z]{2,}-[A-Z]{2,}-[A-Z]+)/i);
                                if (instIdMatch && !row[0].includes('技术指标')) {
                                    instId = instIdMatch[1];
                                    // console.log('[技术指标检测] 从行中提取 instId:', instId);
                                }
                            }
                        }
                    }
                }
            } catch (e) {
                // JSON 解析失败，继续用正则检测原始文本
                // console.log('[技术指标检测] JSON 解析失败，使用正则检测');
            }

            // console.log('[技术指标检测] 特征匹配:', { hasTitlePattern: !!titleMatch, hasIndicatorRow, hasOHLCRow });

            // 如果满足技术指标表格的特征
            if ((hasIndicatorRow && hasOHLCRow) || (titleMatch)) {
                // 优先使用提取的标题信息（按顺序匹配）
                if (extractedTitles && extractedTitles.length > 0 && (!instId || !timeframe)) {
                    // 使用第一个匹配的标题
                    const firstTitle = extractedTitles[0];
                    if (!instId) instId = firstTitle.instId;
                    if (!timeframe) timeframe = firstTitle.timeframe;
                    // console.log('[技术指标检测] 从 extractedTitles 匹配到:', { instId, timeframe });
                }

                // 如果还没有找到 timeframe，尝试从 segmentTitle 中获取
                if (!timeframe && segmentTitle) {
                    const segTimeframe = segmentTitle.match(/(\d+[a-zA-Z]+)/i);
                    if (segTimeframe) {
                        timeframe = segTimeframe[1].toLowerCase();
                        // console.log('[技术指标检测] 从 segmentTitle 匹配到 timeframe:', timeframe);
                    }
                }

                // 如果还没有找到 instId，尝试从 segmentTitle 中获取
                if (!instId && segmentTitle) {
                    const segInstId = segmentTitle.match(/([A-Z]{2,}-[A-Z]{2,}-[A-Z]+)/i);
                    if (segInstId) {
                        instId = segInstId[1];
                        // console.log('[技术指标检测] 从 segmentTitle 匹配到 instId:', instId);
                    }
                }

                if (instId && timeframe) {
                    const result: { isTI: boolean; data?: TechnicalIndicatorChartData } = {
                        isTI: true,
                        data: {
                            instId,
                            timeframe,
                            type: 'TechnicalIndicator' as const,
                            tableData: tableText
                        }
                    };
                    // console.log('[技术指标检测] ✅ 检测到技术指标表格:', result.data);
                    return result;
                } else {
                    // 即使没有完整的 instId 和 timeframe，只要有技术指标特征也显示按钮
                    if (hasIndicatorRow && hasOHLCRow) {
                        // console.log('[技术指标检测] ⚠️ 检测到技术指标表格但缺少完整信息，显示按钮:', {
                        //     instId: instId || '未知',
                        //     timeframe: timeframe || '未知',
                        //     hasIndicatorRow,
                        //     hasOHLCRow
                        // });
                        // 返回一个标记，表示这是技术指标表格，但需要用户手动配置
                        const fallbackResult: { isTI: boolean; data?: TechnicalIndicatorChartData } = {
                            isTI: true,
                            data: {
                                instId: instId || 'UNKNOWN',
                                timeframe: timeframe || 'UNKNOWN',
                                type: 'TechnicalIndicator' as const,
                                tableData: tableText
                            }
                        };
                        return fallbackResult;
                    }
                    // console.log('[技术指标检测] ⚠️ 缺少 instId 或 timeframe:', { instId, timeframe, hasIndicatorRow, hasOHLCRow, segmentTitle });
                }
            } else {
                // console.log('[技术指标检测] ❌ 不满足技术指标表格特征');
            }
        } catch (error) {
            // console.error('检测技术指标表格失败:', error);
        }
        return {isTI: false};
    };
};

// 独立的代码块组件（移到 MarkdownRenderer 外部）
interface CodeBlockComponentProps {
    children: any;
    inline?: boolean;
    className?: string;
    isUser: boolean;
    chartMode?: 'json' | 'chart';
    onToggleChartMode?: () => void;
    onChartModeChange?: (isChart: boolean) => void;
}

const CodeBlockComponent: React.FC<CodeBlockComponentProps> = ({
                                                                   children,
                                                                   inline,
                                                                   className,
                                                                   isUser,
                                                                   chartMode = 'json',
                                                                   onToggleChartMode,
                                                                   onChartModeChange
                                                               }) => {
    // 获取代码内容
    let codeContent = '';
    if (typeof children === 'string') {
        codeContent = children;
    } else if (Array.isArray(children)) {
        codeContent = children.join('');
    } else if (children && typeof children === 'object') {
        codeContent = String(children);
    }

    // 检查是否为JSON代码块
    const isJsonBlock = className?.includes('json') || isJsonContent(codeContent);

    // 检测是否为 FundamentalAnalysis 类型
    const faResult = isJsonBlock ? isFundamentalAnalysis(codeContent) : {isFA: false};

    // 获取代码块背景色
    const codeBlockBackgroundColor = isUser ? 'rgba(24, 144, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';

    // 如果是图表模式，渲染 K 线图
    if (chartMode === 'chart' && faResult.isFA && faResult.data) {
        return (
            <FundamentalAnalysisChart
                data={faResult.data}
                height={350}
                onBack={() => {
                    onToggleChartMode?.();
                    onChartModeChange?.(false);
                }}
            />
        );
    }

    // 如果是JSON，进行格式化和语法高亮
    if (isJsonBlock && !inline) {
        const formattedJson = formatJson(codeContent);
        const highlightedJson = highlightJson(formattedJson);

        return (
            <div style={{position: 'relative', width: '100%', maxWidth: '100%', minWidth: 0}}>
                {/* 可视化按钮 - 仅在 FundamentalAnalysis 类型时显示 */}
                {faResult.isFA && (
                    <Button
                        type="text"
                        size="small"
                        icon={<LineChartOutlined/>}
                        onClick={() => {
                            onToggleChartMode?.();
                            onChartModeChange?.(true);
                        }}
                        style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 10,
                            color: '#F0B429',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '4px'
                        }}
                        title="K线图可视化"
                    >
                        可视化
                    </Button>
                )}
                <pre
                    className="json-code-block"
                    style={{
                        backgroundColor: codeBlockBackgroundColor,
                        border: 'none',
                        borderRadius: '6px',
                        padding: '16px',
                        margin: '16px 0',
                        overflowX: 'auto',
                        fontSize: '13px',
                        fontFamily: 'Consolas, Monaco, monospace',
                        lineHeight: '1.5',
                        color: '#d4d4d4',
                        paddingTop: faResult.isFA ? '44px' : '16px',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box'
                    }}
                    dangerouslySetInnerHTML={{__html: `<code>${highlightedJson}</code>`}}
                />
            </div>
        );
    }

    // 非JSON代码块，使用原有逻辑
    if (inline) {
        return (
            <code
                style={{
                    backgroundColor: codeBlockBackgroundColor,
                    color: '#e6e6e6',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'Monaco, Consolas, monospace'
                }}
            >
                {children}
            </code>
        );
    }

    return (
        <pre
            style={{
                backgroundColor: codeBlockBackgroundColor,
                border: 'none',
                borderRadius: '6px',
                padding: '16px',
                margin: '16px 0',
                overflow: 'auto',
                fontSize: '12px',
                fontFamily: 'Monaco, Consolas, monospace',
                lineHeight: '1.5',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box'
            }}
        >
            <code>{children}</code>
        </pre>
    );
};

interface MarkdownRendererProps {
    content: string;
    style?: React.CSSProperties;
    showChartButtons?: boolean;
    onTableChartClick?: (tableData: string, tableIndex: number) => void;
    segmentTitle?: string;
    showCopyButton?: boolean;
    isUser?: boolean;
    fullContent?: string;
    onChartModeChange?: (isChart: boolean) => void;
    compact?: boolean;
}

const areMarkdownRendererPropsEqual = (prev: MarkdownRendererProps, next: MarkdownRendererProps): boolean => {
    return prev.content === next.content
        && prev.style === next.style
        && prev.showChartButtons === next.showChartButtons
        && prev.segmentTitle === next.segmentTitle
        && prev.showCopyButton === next.showCopyButton
        && prev.isUser === next.isUser
        && prev.fullContent === next.fullContent
        && prev.compact === next.compact;
};

/**
 * MarkdownRenderer组件
 * 用于渲染markdown内容，支持表格、代码块等元素
 * 适配项目暗色主题
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = React.memo(({
                                                                          content,
                                                                          style,
                                                                          showChartButtons = false,
                                                                          onTableChartClick,
                                                                          segmentTitle,
                                                                          showCopyButton = true,
                                                                          isUser = false,
                                                                          fullContent,
                                                                          onChartModeChange,
                                                                          compact = false
                                                                      }) => {
    // 使用计数器触发重渲染
    const [, forceUpdate] = useState(0);

    // 从内容中提取的标题信息（用于技术指标表格检测）
    const extractedTitles = React.useMemo(() => {
        const titles: Array<{ instId: string; timeframe: string; title: string }> = [];

        // 匹配 ### 或 ## 开头的标题，寻找技术指标表格标题
        const titleRegex = /#+\s*([A-Z]{2,}-[A-Z]{2,}-[A-Z]+)-(\d+[a-zA-Z]+)\s*技术指标表格/gi;
        let match;

        while ((match = titleRegex.exec(content)) !== null) {
            titles.push({
                instId: match[1],
                timeframe: match[2].toLowerCase(),
                title: match[0]
            });
        }

        // console.log('[MarkdownRenderer] 从内容中提取的标题:', titles);
        return titles;
    }, [content]);

    // 切换代码块的图表模式
    const toggleChartMode = (codeHash: string) => {
        const currentMode = globalChartModes.get(codeHash) || 'json';
        globalChartModes.set(codeHash, currentMode === 'json' ? 'chart' : 'json');
        forceUpdate(n => n + 1);
    };

    // 获取图表模式
    const getChartMode = (codeHash: string): 'json' | 'chart' => {
        return globalChartModes.get(codeHash) || 'json';
    };

    // 移除 isHovered 和 copied 状态，因为 CollapsibleMessageContent 已经有复制功能

    // 预处理内容函数：将 <thinking> 标签转换为 markdown 引用块格式，处理纯JSON
    const preprocessContent = (content: string, segmentTitle?: string): string => {
        // 1. 先提取thinking内容并转换为markdown引用块格式
        let thinkingMarkdown = '';
        const resultWithThinking = content.replace(/<thinking>([\s\S]*?)<\/thinking>/gi, (match, thinkingContent) => {
            const quotedContent = thinkingContent.trim()
                .split('\n')
                .map((line: string) => `> ${line}`)
                .join('\n');
            thinkingMarkdown = `**🤔 思考过程:**\n${quotedContent}`;
            return ''; // 移除thinking标签
        });

        // 2. 检测剩余内容是否为JSON
        let result = resultWithThinking.trim();
        if (!result.includes('```json') && !result.includes('```JSON')) {
            if (result.startsWith('{') || result.startsWith('[')) {
                result = '```json\n' + result + '\n```';
            }
        }

        // 3. 组合thinking和JSON内容
        if (thinkingMarkdown) {
            return thinkingMarkdown + '\n\n' + result;
        }
        return result;
    };

    // 静态计数器，用于为每个表格生成唯一索引，避免状态重渲染
    let staticTableCounter = 0;

    // 获取背景色，根据isUser参数返回不同的背景色
    const getBackgroundColor = () => {
        if (isUser) {
            return 'transparent';
        }
        return '#2a2a2a';
    };

    // 获取代码块背景色
    const getCodeBlockBackgroundColor = () => {
        if (isUser) {
            return 'rgba(24, 144, 255, 0.3)';
        }
        return 'rgba(0, 0, 0, 0.3)';
    };

    // 获取表格背景色
    const getTableBackgroundColor = () => {
        if (isUser) {
            return 'rgba(24, 144, 255, 0.3)';
        }
        return 'rgba(255, 255, 255, 0.1)';
    };

    // 获取表头背景色
    const getTableHeaderBackgroundColor = () => {
        if (isUser) {
            return 'rgba(24, 144, 255, 0.3)';
        }
        return 'rgba(255, 255, 255, 0.15)';
    };

    // 获取表格行背景色
    const getTableRowBackgroundColor = (index: number) => {
        if (isUser) {
            return 'rgba(24, 144, 255, 0.3)';
        }
        return index % 2 === 0 ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.12)';
    };

    // 判断段落是否应该显示图表按钮
    const shouldShowChartForSegment = (): boolean => {
        if (!showChartButtons || !segmentTitle) return false;

        const title = segmentTitle.toLowerCase();
        const positionKeywords = ['持仓', 'position', '价格', 'price', '仓位', 'portfolio'];
        const indicatorKeywords = ['技术指标', '技术分析', 'indicator', 'ema', 'sma', 'rsi', 'boll', 'bollinger', '均线', '移动平均', 'macd', 'kdj', 'cci'];

        return positionKeywords.some(keyword => title.includes(keyword)) ||
            indicatorKeywords.some(keyword => title.includes(keyword));
    };

    // 获取表格数据类型
    const getTableType = (): 'position' | 'indicator' | null => {
        if (!segmentTitle) return null;

        const title = segmentTitle.toLowerCase();
        const positionKeywords = ['持仓', 'position', '价格', 'price', '仓位', 'portfolio'];
        const indicatorKeywords = ['技术指标', '技术分析', 'indicator', 'ema', 'sma', 'rsi', 'boll', 'bollinger', '均线', '移动平均', 'macd', 'kdj', 'cci'];

        if (positionKeywords.some(keyword => title.includes(keyword))) return 'position';
        if (indicatorKeywords.some(keyword => title.includes(keyword))) return 'indicator';
        return null;
    };

    // 表格数据结构定义
    interface TableData {
        headers: string[];
        rows: string[][];
    }


    // 处理表格数据，返回结构化的JSON数据
    const extractTableData = (element: React.ReactNode): string => {
        try {
            if (!element) {
                // console.warn('表格元素为空');
                return JSON.stringify({headers: [], rows: []});
            }

            const tableData: TableData = {headers: [], rows: []};

            // 处理数组类型的元素（ReactMarkdown可能传入数组）
            const processNode = (node: React.ReactNode): void => {
                // 如果是数组，递归处理每个元素
                if (Array.isArray(node)) {
                    node.forEach(processNode);
                    return;
                }

                // 如果是React元素，处理其结构
                if (React.isValidElement(node)) {
                    const elementType = node.type;
                    const props = node.props;

                    // 获取组件名称（支持函数组件和字符串标签）
                    let componentName = '';
                    if (typeof elementType === 'string') {
                        componentName = elementType.toLowerCase();
                    } else {
                        // 对于函数组件，尝试获取displayName或函数名
                        componentName = (elementType as any)?.displayName?.toLowerCase() ||
                            (elementType as any)?.name?.toLowerCase() ||
                            '';
                    }

                    // 检查是否是表格相关元素
                    switch (componentName) {
                        case 'table':
                        case 'tablecomponent':
                            // 直接处理表格的children
                            if (props.children) {
                                processNode(props.children);
                            }
                            break;

                        case 'thead':
                        case 'theadcomponent':
                            // 处理表头
                            if (props.children) {
                                extractHeaders(props.children);
                            }
                            break;

                        case 'tbody':
                        case 'tbodycomponent':
                            // 处理表体
                            if (props.children) {
                                extractRows(props.children);
                            }
                            break;

                        case 'tr':
                        case 'rowcomponent':
                            // 处理表格行：重置行单元格收集器，提取单元格内容，然后保存到rows
                            currentRowCells = [];
                            if (props.children) {
                                extractRowCells(props.children);
                            }
                            if (currentRowCells.length > 0) {
                                tableData.rows.push([...currentRowCells]);
                            }
                            currentRowCells = [];
                            break;

                        case 'th':
                        case 'thcomponent':
                            // 处理表头单元格
                            const thText = extractTextFromNode(node);
                            if (thText && thText.trim()) {
                                tableData.headers.push(thText.trim());
                            }
                            break;

                        case 'td':
                        case 'tdcomponent':
                            // 处理表格单元格
                            const tdText = extractTextFromNode(node);
                            if (tdText && tdText.trim()) {
                                currentRowCells.push(tdText.trim());
                            }
                            break;

                        default:
                            // 对于其他元素，继续递归处理
                            if (props.children) {
                                processNode(props.children);
                            }
                            break;
                    }
                }
            };

            let currentRowCells: string[] = [];

            const extractHeaders = (node: React.ReactNode): void => {
                const headers: string[] = [];
                let inHeaderRow = false;

                const traverseHeaders = (n: React.ReactNode): void => {
                    if (Array.isArray(n)) {
                        n.forEach(traverseHeaders);
                        return;
                    }

                    if (React.isValidElement(n)) {
                        const elementType = n.type;
                        let componentName = '';

                        if (typeof elementType === 'string') {
                            componentName = elementType.toLowerCase();
                        } else {
                            componentName = (elementType as any)?.displayName?.toLowerCase() ||
                                (elementType as any)?.name?.toLowerCase() || '';
                        }

                        // 检查是否是表头行
                        if (componentName === 'tr' || componentName === 'rowcomponent') {
                            inHeaderRow = true;
                            if (n.props.children) {
                                traverseHeaders(n.props.children);
                            }
                            inHeaderRow = false;
                        } else if ((componentName === 'th' || componentName === 'thcomponent') && inHeaderRow) {
                            const text = extractTextFromNode(n);
                            if (text && text.trim()) {
                                headers.push(text.trim());
                            }
                        } else if (n.props.children) {
                            traverseHeaders(n.props.children);
                        }
                    }
                };

                traverseHeaders(node);
                tableData.headers = headers;
            };

            const extractRows = (node: React.ReactNode): void => {
                const rows: string[][] = [];

                const traverseRows = (n: React.ReactNode): void => {
                    if (Array.isArray(n)) {
                        n.forEach(traverseRows);
                        return;
                    }

                    if (React.isValidElement(n)) {
                        const elementType = n.type;
                        let componentName = '';

                        if (typeof elementType === 'string') {
                            componentName = elementType.toLowerCase();
                        } else {
                            componentName = (elementType as any)?.displayName?.toLowerCase() ||
                                (elementType as any)?.name?.toLowerCase() || '';
                        }

                        if (componentName === 'tr' || componentName === 'rowcomponent') {
                            currentRowCells = [];
                            if (n.props.children) {
                                extractRowCells(n.props.children);
                            }
                            if (currentRowCells.length > 0) {
                                rows.push([...currentRowCells]);
                            }
                            currentRowCells = [];
                        } else if (n.props.children) {
                            traverseRows(n.props.children);
                        }
                    }
                };

                traverseRows(node);
                tableData.rows = rows;
            };

            const extractRowCells = (trChildren: React.ReactNode): void => {
                if (Array.isArray(trChildren)) {
                    trChildren.forEach((child) => {
                        if (React.isValidElement(child)) {
                            const elementType = child.type;
                            let componentName = '';

                            if (typeof elementType === 'string') {
                                componentName = elementType.toLowerCase();
                            } else {
                                componentName = (elementType as any)?.displayName?.toLowerCase() ||
                                    (elementType as any)?.name?.toLowerCase() || '';
                            }

                            if (componentName === 'td' || componentName === 'tdcomponent') {
                                const text = extractTextFromNode(child);
                                if (text && text.trim()) {
                                    currentRowCells.push(text.trim());
                                }
                            } else if ((child.props as any)?.children) {
                                extractRowCells((child.props as any).children);
                            }
                        }
                    });
                }
            };

            const extractTextFromNode = (node: React.ReactNode): string => {
                if (node === null || node === undefined) return '';
                if (typeof node === 'string') return node;
                if (typeof node === 'number') return node.toString();
                if (typeof node === 'boolean') return node.toString();

                if (Array.isArray(node)) {
                    return node.map(extractTextFromNode).join('');
                }

                if (React.isValidElement(node) && node.props) {
                    if (node.props.children) {
                        return extractTextFromNode(node.props.children);
                    }
                }

                return '';
            };

            // 开始处理传入的元素
            processNode(element);

            // 如果结构化解析无法获取表头或数据行，尝试从文本解析
            if (tableData.headers.length === 0 || tableData.rows.length === 0) {
                const textContent = extractTextFromNode(element);
                const parsedData = parseTableFromText(textContent);

                // 如果文本解析成功获取了数据则合并（不会覆盖结构化解析已有的数据）
                if (parsedData.headers.length > 0) {
                    if (tableData.headers.length === 0) {
                        tableData.headers = parsedData.headers;
                    }
                    if (tableData.rows.length === 0) {
                        tableData.rows = parsedData.rows;
                    } else if (parsedData.rows.length > 0) {
                        // headers为空但rows有数据时：说明结构化解析漏掉了表头行和部分行
                        // 此时用文本解析的结果完全替换（因为headers错位会导致OHLC检测失败）
                        tableData.headers = parsedData.headers;
                        tableData.rows = parsedData.rows;
                    }
                }
            }

            // console.log('解析到的表格数据:', tableData);
            return JSON.stringify(tableData);

        } catch (error) {
            // console.error('表格数据提取失败:', error);
            return JSON.stringify({headers: [], rows: []});
        }
    };

    // 从纯文本解析表格数据的备用方案
    const parseTableFromText = (text: string): TableData => {
        const result: TableData = {headers: [], rows: []};

        try {
            if (!text || text.trim().length === 0) {
                return result;
            }

            // 清理文本，移除多余的空格和换行
            const cleanText = text.trim().replace(/\s+/g, ' ');

            // 第一步：识别表头
            // 表头通常以"指标"开头，后面跟着时间列
            const timePattern = /(\d{1,2}:\d{2})/g;
            const timeMatches = cleanText.match(timePattern) || [];

            // 查找指标类型（如"指标(5分钟)"）
            const indicatorMatch = cleanText.match(/指标\([^)]*\)/);
            const indicatorType = indicatorMatch ? indicatorMatch[0] : '指标';

            if (timeMatches.length > 0) {
                // 构建表头
                result.headers = [indicatorType, ...timeMatches];

                // 第二步：提取数据行
                // 使用正则表达式识别数据行
                const rowPatterns = [
                    {name: 'open', regex: /open([\d,.-]+)/g},
                    {name: 'high', regex: /high([\d,.-]+)/g},
                    {name: 'low', regex: /low([\d,.-]+)/g},
                    {name: 'close', regex: /close([\d,.-]+)/g},
                    {name: 'volume', regex: /volume([\d,.-]+)/g}
                ];

                // 添加技术指标模式
                const technicalIndicators = [
                    {name: 'SMA', regex: /SMA\([^)]*\)([\d,.-]+)/g},
                    {name: 'EMA', regex: /EMA\([^)]*\)([\d,.-]+)/g},
                    {name: 'RSI', regex: /RSI\([^)]*\)([\d,.-]+)/g},
                    {name: 'BOLL(20)-UB', regex: /BOLL\([^)]*\)-UB([\d,.-]+)/g},
                    {name: 'BOLL(20)-MB', regex: /BOLL\([^)]*\)-MB([\d,.-]+)/g},
                    {name: 'BOLL(20)-LB', regex: /BOLL\([^)]*\)-LB([\d,.-]+)/g}
                ];

                rowPatterns.push(...technicalIndicators);

                for (const pattern of rowPatterns) {
                    const matches = [...text.matchAll(pattern.regex)];
                    if (matches.length > 0) {
                        // 取最后一个匹配，因为可能包含多个匹配
                        const lastMatch = matches[matches.length - 1];
                        const values = lastMatch[1];

                        // 分割数值（基于时间匹配的数量）
                        const valueArray = splitValuesByTimeCount(values, timeMatches.length);

                        if (valueArray.length === timeMatches.length) {
                            result.rows.push([pattern.name, ...valueArray]);
                        }
                    }
                }
            }

            // console.log('从文本解析的表格数据:', result);
            return result;

        } catch (error) {
            // console.error('从文本解析表格失败:', error);
            return result;
        }
    };

    // 根据时间数量分割数值
    const splitValuesByTimeCount = (values: string, timeCount: number): string[] => {
        const result: string[] = [];

        // 移除逗号，保留小数点和负号
        const cleanValues = values.replace(/,/g, ' ');

        // 使用正则表达式分割数字
        const numberPattern = /-?\d+\.?\d*/g;
        const numberMatches = cleanValues.match(numberPattern) || [];

        // 如果匹配的数量正好等于时间数量，直接返回
        if (numberMatches.length === timeCount) {
            return numberMatches;
        }

        // 如果数量不匹配，尝试按比例分割
        if (numberMatches.length > timeCount) {
            const ratio = Math.floor(numberMatches.length / timeCount);
            for (let i = 0; i < timeCount; i++) {
                const startIndex = i * ratio;
                const endIndex = Math.min((i + 1) * ratio, numberMatches.length);
                const segment = numberMatches.slice(startIndex, endIndex);
                result.push(segment[0]); // 取每个段落的第一个值
            }
        } else if (numberMatches.length > 0) {
            // 如果数值不够，使用现有的数值
            result.push(...numberMatches);
            // 补齐空值
            while (result.length < timeCount) {
                result.push('');
            }
        }

        return result.slice(0, timeCount);
    };
    // 自定义表格样式组件
    const TableComponent: React.FC<any> = React.memo(({children}) => {
        // 使用 useRef 存储表格索引，避免重渲染
        const tableIndexRef = React.useRef<number>();

        // 只在第一次挂载时分配索引
        if (tableIndexRef.current === undefined) {
            tableIndexRef.current = staticTableCounter++;
        }

        // console.log('[TableComponent] 渲染表格 #', tableIndexRef.current, 'segmentTitle:', segmentTitle);

        // 创建技术指标检测器（捕获当前 segmentTitle 和 extractedTitles）
        const isTechnicalIndicatorTable = createTechnicalIndicatorTableDetector(segmentTitle, extractedTitles);

        const showChartButton = shouldShowChartForSegment();

        // 提取表格数据（extractTableData 返回的已经是 JSON 字符串）
        const tableData = extractTableData(children);
        const tableType = getTableType();

        // console.log('[TableComponent] 提取的表格数据:', tableData.substring(0, 200));

        // 检测是否为技术指标表格（tableData 已经是 JSON 字符串，不需要再 stringify）
        const tiResult = isTechnicalIndicatorTable(tableData);

        // console.log('[TableComponent] 技术指标检测结果:', tiResult);

        // 获取表格图表模式
        const tableKey = `table-${tableIndexRef.current}`;
        const isChartMode = globalTableChartModes.get(tableKey) || false;

        const handleChartClick = () => {
            if (onTableChartClick) {
                // 有回调时（Bot页面），所有表格都通过回调打开弹窗
                onTableChartClick(tableData, tableIndexRef.current || 0);
            } else if (tiResult.isTI) {
                // 没有回调时（AI Chat），技术指标表格直接替换显示
                const newMode = !isChartMode;
                globalTableChartModes.set(tableKey, newMode);
                // 通知外层容器扩展/收缩宽度
                onChartModeChange?.(newMode);
                forceUpdate(n => n + 1);
            }
        };

        // 如果是技术指标表格且处于图表模式，且没有onTableChartClick回调（AI Chat场景），渲染 K 线图
        if (tiResult.isTI && tiResult.data && isChartMode && !onTableChartClick) {
            return (
                <div style={{position: 'relative', marginBottom: '16px', width: '100%'}}>
                    <TechnicalIndicatorChart
                        data={tiResult.data}
                        height={350}
                        onBack={() => {
                            globalTableChartModes.set(tableKey, false);
                            // 收缩外层容器宽度
                            onChartModeChange?.(false);
                            forceUpdate(n => n + 1);
                        }}
                    />
                </div>
            );
        }

        const tableStyle: React.CSSProperties = compact ? {
            borderCollapse: 'collapse',
            backgroundColor: getTableBackgroundColor(),
            borderRadius: '6px',
            overflow: 'hidden',
            border: '1px solid var(--pt-border)',
            width: '100%',
            tableLayout: 'fixed'
        } : {
            borderCollapse: 'collapse',
            backgroundColor: getTableBackgroundColor(),
            borderRadius: '6px',
            overflow: 'hidden',
            border: '1px solid var(--pt-border)',
            minWidth: '600px'
        };

        return (
            <div style={{position: 'relative', marginBottom: '16px', width: '100%'}}>
                <div style={{
                    backgroundColor: getTableBackgroundColor(),
                    width: '100%',
                    ...(compact ? {} : {overflowX: 'auto'})
                }}>
                    <table style={tableStyle}>
                        {children}
                    </table>
                </div>
                {(showChartButton || tiResult.isTI) && (
                    <Button
                        type="text"
                        size="small"
                        icon={<LineChartOutlined/>}
                        onClick={handleChartClick}
                        style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            backgroundColor: 'rgba(26, 26, 26, 0.9)',
                            borderColor: 'var(--pt-border)',
                            color: '#F0B429',
                            fontSize: '12px',
                            height: '24px',
                            padding: '0 6px',
                            zIndex: 10,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            backdropFilter: 'blur(4px)'
                        }}
                        title={`${tableType === 'position' ? '持仓价格' : '技术指标'}K 线图`}
                    />
                )}
            </div>
        );
    });


    TableComponent.displayName = 'TableComponent';

    // 自定义表头样式组件
    const TheadComponent: React.FC<any> = ({children}) => (
        <thead
            style={{
                backgroundColor: getTableHeaderBackgroundColor(),
                borderBottom: '2px solid var(--pt-border)'
            }}
        >
        {children}
        </thead>
    );
    TheadComponent.displayName = 'TheadComponent';

    // 自定义表格行样式组件
    const RowComponent: React.FC<any> = ({children, index}) => (
        <tr
            style={{
                backgroundColor: getTableRowBackgroundColor(index),
                borderBottom: '1px solid var(--pt-border)'
            }}
        >
            {children}
        </tr>
    );
    RowComponent.displayName = 'RowComponent';

    // 自定义表头单元格样式组件
    const ThComponent: React.FC<any> = ({children}) => (
        <th
            style={compact ? {
                padding: '8px',
                textAlign: 'left',
                borderRight: '1px solid var(--pt-border)',
                color: '#ffffff',
                fontWeight: '600',
                fontSize: '12px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            } : {
                padding: '12px',
                textAlign: 'left',
                borderRight: '1px solid var(--pt-border)',
                color: '#ffffff',
                fontWeight: '600',
                fontSize: '13px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: '100px'
            }}
        >
            {children}
        </th>
    );
    ThComponent.displayName = 'ThComponent';

    // 自定义表格单元格样式组件
    const TdComponent: React.FC<any> = ({children}) => (
        <td
            style={compact ? {
                padding: '8px',
                borderRight: '1px solid var(--pt-border)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '12px',
                lineHeight: '1.5',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            } : {
                padding: '12px',
                borderRight: '1px solid var(--pt-border)',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '13px',
                lineHeight: '1.5',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: '100px'
            }}
        >
            {children}
        </td>
    );
    TdComponent.displayName = 'TdComponent';

    // 自定义链接样式组件
    const LinkComponent: React.FC<any> = ({href, children}) => (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                color: '#F0B429',
                textDecoration: 'none'
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.textDecoration = 'none';
            }}
        >
            {children}
        </a>
    );

    return (
        <div
            style={{
                position: 'relative',
                backgroundColor: getBackgroundColor(),
                color: 'rgba(255, 255, 255, 0.9)',
                padding: '8px',
                borderRadius: '6px',
                fontSize: '13px',
                lineHeight: '1.2',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'visible',
                ...style
            }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    table: TableComponent,
                    thead: TheadComponent,
                    tbody: ({children}) => <tbody>{children}</tbody>,
                    tr: RowComponent,
                    th: ThComponent,
                    td: TdComponent,
                    code: (props: any) => {
                        // 获取代码内容生成 hash
                        let codeContent = '';
                        if (typeof props.children === 'string') {
                            codeContent = props.children;
                        } else if (Array.isArray(props.children)) {
                            codeContent = props.children.join('');
                        } else if (props.children && typeof props.children === 'object') {
                            codeContent = String(props.children);
                        }
                        const codeHash = simpleHash(codeContent);
                        return (
                            <CodeBlockComponent
                                {...props}
                                isUser={isUser}
                                chartMode={getChartMode(codeHash)}
                                onToggleChartMode={() => toggleChartMode(codeHash)}
                                onChartModeChange={onChartModeChange}
                            />
                        );
                    },
                    a: LinkComponent,
                    // 其他markdown元素的样式
                    h1: ({children}) => (
                        <h1
                            style={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontSize: '24px',
                                fontWeight: '600',
                                margin: '12px 0 8px 0',
                                borderBottom: '1px solid var(--pt-border)',
                                paddingBottom: '2px'
                            }}
                        >
                            {children}
                        </h1>
                    ),
                    h2: ({children}) => (
                        <h2
                            style={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontSize: '20px',
                                fontWeight: '600',
                                margin: '8px 0 4px 0',
                                borderBottom: '1px solid var(--pt-border)',
                                paddingBottom: '2px'
                            }}
                        >
                            {children}
                        </h2>
                    ),
                    h3: ({children}) => (
                        <h3
                            style={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontSize: '18px',
                                fontWeight: '600',
                                margin: '8px 0 4px 0'
                            }}
                        >
                            {children}
                        </h3>
                    ),
                    h4: ({children}) => (
                        <h4
                            style={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontSize: '16px',
                                fontWeight: '600',
                                margin: '6px 0 4px 0'
                            }}
                        >
                            {children}
                        </h4>
                    ),
                    p: ({children}) => (
                        <p
                            style={{
                                margin: '4px 0',
                                lineHeight: '1.6'
                            }}
                        >
                            {children}
                        </p>
                    ),
                    ul: ({children}) => (
                        <ul
                            style={{
                                margin: '4px 0',
                                paddingLeft: '20px'
                            }}
                        >
                            {children}
                        </ul>
                    ),
                    ol: ({children}) => (
                        <ol
                            style={{
                                margin: '4px 0',
                                paddingLeft: '20px'
                            }}
                        >
                            {children}
                        </ol>
                    ),
                    li: ({children}) => (
                        <li
                            style={{
                                margin: '2px 0',
                                lineHeight: '1.6'
                            }}
                        >
                            {children}
                        </li>
                    ),
                    blockquote: ({children}) => (
                        <blockquote
                            style={{
                                borderLeft: '4px solid #F0B429',
                                padding: '8px 12px',
                                margin: '8px 0',
                                backgroundColor: getTableBackgroundColor(),
                                borderRadius: '0 6px 6px 0',
                                fontStyle: 'italic',
                                color: 'rgba(255, 255, 255, 0.8)'
                            }}
                        >
                            {children}
                        </blockquote>
                    ),
                    strong: ({children}) => (
                        <strong
                            style={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontWeight: '600'
                            }}
                        >
                            {children}
                        </strong>
                    ),
                    em: ({children}) => (
                        <em
                            style={{
                                color: 'rgba(255, 255, 255, 0.85)',
                                fontStyle: 'italic'
                            }}
                        >
                            {children}
                        </em>
                    ),
                    hr: () => (
                        <hr
                            style={{
                                border: 'none',
                                borderTop: '1px solid var(--pt-border)',
                                margin: '12px 0'
                            }}
                        />
                    )
                }}
            >
                {preprocessContent(content, segmentTitle)}
            </ReactMarkdown>
        </div>
    );
}, areMarkdownRendererPropsEqual);

export default MarkdownRenderer;
